import React, { useEffect, useRef, useState } from 'react';

import { MapContainer, TileLayer, Marker, useMap, useMapEvents } from 'react-leaflet';

import L from 'leaflet';

import 'leaflet/dist/leaflet.css';

/* =========================================================
   CUSTOM MARKER — a proper teardrop pin instead of Leaflet's
   default icon (skips the marker-icon.png/2x/shadow image
   imports entirely). Styled with .location-pin-icon below.
========================================================= */

const locationPinIcon = L.divIcon({
    className: 'location-pin-icon',
    html: `<svg width="34" height="44" viewBox="0 0 34 44" xmlns="http://www.w3.org/2000/svg">
        <path d="M17 0C7.61 0 0 7.61 0 17c0 12.75 17 27 17 27s17-14.25 17-27C34 7.61 26.39 0 17 0z" fill="#2563eb"/>
        <circle cx="17" cy="17" r="6.5" fill="#ffffff"/>
    </svg>`,
    iconSize: [34, 44],
    iconAnchor: [17, 44],
    popupAnchor: [0, -40]
});

/* =========================================================
   DEFAULT MAP LOCATION
========================================================= */

const DEFAULT_CENTER = [20.5937, 78.9629];

/* =========================================================
   MAP CLICK HANDLER
========================================================= */

const MapClickHandler = ({ disabled, onLocationChange }) => {
    useMapEvents({
        click(event) {
            if (disabled) return;

            const { lat, lng } = event.latlng;

            onLocationChange(lat, lng);
        }
    });

    return null;
};

/* =========================================================
   SEARCH RESULT MAP CONTROLLER
========================================================= */

const MapController = ({ latitude, longitude }) => {
    const map = useMap();

    useEffect(() => {
        const refreshMap = () => {
            map.invalidateSize();
        };

        const timer = setTimeout(() => {
            map.invalidateSize();

            if (typeof latitude === 'number' && typeof longitude === 'number') {
                map.flyTo([latitude, longitude], 16, {
                    duration: 1.2
                });
            }
        }, 150);

        window.addEventListener('resize', refreshMap);

        return () => {
            clearTimeout(timer);

            window.removeEventListener('resize', refreshMap);
        };
    }, [latitude, longitude, map]);

    return null;
};

/* =========================================================
   LOCATION SEARCH
========================================================= */

const LocationSearch = ({ disabled, onLocationChange, countryCode }) => {
    const [query, setQuery] = useState('');
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showResults, setShowResults] = useState(false);
    const [activeIndex, setActiveIndex] = useState(-1);
    const containerRef = useRef(null);

    const map = useMap();

    /* -----------------------------------------------------
       SEARCH NOMINATIM
    ----------------------------------------------------- */

    useEffect(() => {
        if (disabled) return;

        if (!query.trim()) {
            setResults([]);
            return;
        }

        const timer = setTimeout(async () => {
            try {
                setLoading(true);

                const params = new URLSearchParams({
                    format: 'jsonv2',
                    limit: '5',
                    addressdetails: '1',
                    q: query
                });

                // Biases results to one country so "Manali" doesn't also
                // surface unrelated places elsewhere. Pass countryCode={null}
                // to search globally instead.
                if (countryCode) {
                    params.set('countrycodes', countryCode);
                }

                const response = await fetch(`https://nominatim.openstreetmap.org/search?${params.toString()}`, {
                    headers: {
                        Accept: 'application/json'
                    }
                });

                if (!response.ok) {
                    throw new Error('Location search failed');
                }

                const data = await response.json();

                setResults(data || []);
                setShowResults(true);
                setActiveIndex(-1);
            } catch (error) {
                console.error('Location search error:', error);

                setResults([]);
            } finally {
                setLoading(false);
            }
        }, 500);

        return () => clearTimeout(timer);
    }, [query, disabled, countryCode]);

    /* -----------------------------------------------------
       CLOSE ON OUTSIDE CLICK
    ----------------------------------------------------- */

    useEffect(() => {
        function handleClickOutside(event) {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                setShowResults(false);
            }
        }

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    /* -----------------------------------------------------
       SELECT RESULT
    ----------------------------------------------------- */

    const handleSelect = (result) => {
        const latitude = Number(result.lat);
        const longitude = Number(result.lon);

        if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
            return;
        }

        setQuery(result.display_name);
        setResults([]);
        setShowResults(false);
        setActiveIndex(-1);

        onLocationChange(latitude, longitude);

        map.flyTo([latitude, longitude], 16, {
            duration: 1.2
        });
    };

    const handleClear = () => {
        setQuery('');
        setResults([]);
        setShowResults(false);
        setActiveIndex(-1);
    };

    /* -----------------------------------------------------
       KEYBOARD NAVIGATION — arrow keys + enter, like a real
       address autocomplete
    ----------------------------------------------------- */

    const handleKeyDown = (event) => {
        if (!showResults || results.length === 0) return;

        if (event.key === 'ArrowDown') {
            event.preventDefault();
            setActiveIndex((prev) => (prev + 1) % results.length);
        } else if (event.key === 'ArrowUp') {
            event.preventDefault();
            setActiveIndex((prev) => (prev - 1 + results.length) % results.length);
        } else if (event.key === 'Enter' && activeIndex >= 0) {
            event.preventDefault();
            handleSelect(results[activeIndex]);
        } else if (event.key === 'Escape') {
            setShowResults(false);
        }
    };

    return (
        <div className='map-search-container' ref={containerRef}>
            <div className={`map-search-box ${disabled ? 'map-search-box--disabled' : ''}`}>
                <span className='map-search-icon'>
                    <svg
                        width='16'
                        height='16'
                        viewBox='0 0 24 24'
                        fill='none'
                        stroke='currentColor'
                        strokeWidth='2'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                    >
                        <circle cx='11' cy='11' r='7' />
                        <line x1='21' y1='21' x2='16.65' y2='16.65' />
                    </svg>
                </span>

                <input
                    type='text'
                    value={query}
                    disabled={disabled}
                    placeholder='Search property, city, area or landmark...'
                    onFocus={() => {
                        if (results.length > 0) {
                            setShowResults(true);
                        }
                    }}
                    onChange={(event) => {
                        setQuery(event.target.value);
                    }}
                    onKeyDown={handleKeyDown}
                    aria-label='Search for a location'
                    aria-expanded={showResults}
                    aria-autocomplete='list'
                />

                {loading && <span className='map-search-spinner' role='status' aria-label='Searching' />}

                {!loading && query && !disabled && (
                    <button type='button' className='map-search-clear' onClick={handleClear} aria-label='Clear search'>
                        <svg
                            width='12'
                            height='12'
                            viewBox='0 0 24 24'
                            fill='none'
                            stroke='currentColor'
                            strokeWidth='2.6'
                            strokeLinecap='round'
                        >
                            <line x1='18' y1='6' x2='6' y2='18' />
                            <line x1='6' y1='6' x2='18' y2='18' />
                        </svg>
                    </button>
                )}
            </div>

            {showResults && results.length > 0 && (
                <div className='map-search-results' role='listbox'>
                    {results.map((result, index) => (
                        <button
                            key={result.place_id}
                            type='button'
                            role='option'
                            aria-selected={index === activeIndex}
                            className={`map-search-result ${index === activeIndex ? 'map-search-result--active' : ''}`}
                            onMouseEnter={() => setActiveIndex(index)}
                            onClick={() => handleSelect(result)}
                        >
                            <span className='result-pin'>
                                <svg width='14' height='14' viewBox='0 0 24 24' fill='currentColor'>
                                    <path d='M12 2C7.58 2 4 5.58 4 10c0 5.25 7 11.5 7.35 11.8a1 1 0 0 0 1.3 0C12 21.5 20 15.25 20 10c0-4.42-3.58-8-8-8zm0 11a3 3 0 1 1 0-6 3 3 0 0 1 0 6z' />
                                </svg>
                            </span>

                            <span className='result-content'>
                                <strong>{result.name || result.display_name.split(',')[0]}</strong>

                                <small>{result.display_name}</small>
                            </span>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

/* =========================================================
   "USE MY CURRENT LOCATION" — the control people expect on
   a location picker now (browser geolocation).
========================================================= */

const CurrentLocationButton = ({ disabled, onLocationChange }) => {
    const [locating, setLocating] = useState(false);
    const [error, setError] = useState('');
    const map = useMap();

    const handleClick = () => {
        if (disabled || !navigator.geolocation) return;

        setLocating(true);
        setError('');

        navigator.geolocation.getCurrentPosition(
            (position) => {
                const { latitude, longitude } = position.coords;

                onLocationChange(latitude, longitude);
                map.flyTo([latitude, longitude], 16, { duration: 1.2 });
                setLocating(false);
            },
            () => {
                setError('Could not get your location');
                setLocating(false);
            },
            { enableHighAccuracy: true, timeout: 8000 }
        );
    };

    return (
        <div className='map-locate-container'>
            {error && <span className='map-locate-error'>{error}</span>}

            <button
                type='button'
                className='map-locate-btn'
                onClick={handleClick}
                disabled={disabled || locating}
                title='Use my current location'
                aria-label='Use my current location'
            >
                {locating ? (
                    <span className='map-search-spinner' />
                ) : (
                    <svg
                        width='18'
                        height='18'
                        viewBox='0 0 24 24'
                        fill='none'
                        stroke='currentColor'
                        strokeWidth='2'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                    >
                        <circle cx='12' cy='12' r='3' />
                        <path d='M12 2v3M12 19v3M2 12h3M19 12h3' />
                    </svg>
                )}
            </button>
        </div>
    );
};

/* =========================================================
   LOCATION MAP
========================================================= */

const LocationMap = ({ latitude, longitude, onLocationChange, disabled = false, countryCode = 'in' }) => {
    const hasValidCoordinates =
        typeof latitude === 'number' &&
        typeof longitude === 'number' &&
        Number.isFinite(latitude) &&
        Number.isFinite(longitude) &&
        latitude >= -90 &&
        latitude <= 90 &&
        longitude >= -180 &&
        longitude <= 180;

    const position = hasValidCoordinates ? [latitude, longitude] : DEFAULT_CENTER;

    return (
        <div className='location-map-wrapper'>
            <MapContainer
                center={position}
                zoom={hasValidCoordinates ? 16 : 5}
                scrollWheelZoom={false}
                className='location-map'
            >
                <TileLayer
                    attribution='&copy; OpenStreetMap contributors'
                    url='https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
                    maxZoom={19}
                    updateWhenIdle={false}
                    updateWhenZooming={true}
                    keepBuffer={3}
                />

                {/* Search box */}
                <LocationSearch disabled={disabled} onLocationChange={onLocationChange} countryCode={countryCode} />

                {/* Use my current location */}
                <CurrentLocationButton disabled={disabled} onLocationChange={onLocationChange} />

                {/* Click map */}
                <MapClickHandler disabled={disabled} onLocationChange={onLocationChange} />

                {/* Move map when location changes */}
                {hasValidCoordinates && <MapController latitude={latitude} longitude={longitude} />}

                {/* Marker */}
                {hasValidCoordinates && (
                    <Marker
                        position={[latitude, longitude]}
                        icon={locationPinIcon}
                        draggable={!disabled}
                        eventHandlers={{
                            dragend: (event) => {
                                if (disabled) return;

                                const marker = event.target;

                                const { lat, lng } = marker.getLatLng();

                                onLocationChange(lat, lng);
                            }
                        }}
                    />
                )}
            </MapContainer>

            {/* =================================================
                HELP TEXT
            ================================================= */}

            <div className='map-help-text'>
                {disabled
                    ? 'Map location cannot be edited.'
                    : hasValidCoordinates
                      ? 'Search for a location, click on the map, or drag the pin to set the exact property location.'
                      : 'Search for the property location or click anywhere on the map.'}
            </div>
        </div>
    );
};

export default LocationMap;
